import { MAX_RECORDING_MINUTES } from "@msuite/utils/videos";
import { Button } from "../button";
import { Icon } from "../icons";
import { useTranslation } from "../localization";
import { ProgressMeter } from "../progress-meter";

/** Props Interface */
export interface RecordingCardProps {
	/** What the destination is called — the page the recording was started from. */
	label: string;
	/** Upload progress as a fraction between 0 and 1. */
	progress: number;
	isUploading: boolean;
	hasFailed: boolean;
	/** Whether the recording ended by reaching the length limit rather than by the author's hand. */
	didReachLimit?: boolean;
	onOpenTarget: () => void;
	onRetry: () => void;
	onDismiss: () => void;
}

/**
 * What the panel becomes once the recording has stopped: the transfer, and the way back.
 *
 * It stays put rather than navigating on its own — the author stopped somewhere in the middle
 * of another task, and an app that teleports them is an app that took the wheel.
 *
 * The line about trimming is deliberate. This wait is exactly when an author wonders whether
 * the take was good enough, and one who does not know the video can still be cut will re-record
 * the whole tutorial instead.
 */
export function RecordingCard({
	label,
	progress,
	isUploading,
	hasFailed,
	didReachLimit = false,
	onOpenTarget,
	onRetry,
	onDismiss,
}: RecordingCardProps) {
	/** Context */
	const { t } = useTranslation();

	/** Render */
	return (
		<div className="card v-stack gap-2 p-3 w-[19rem] shadow-lg">
			<div className="h-stack items-center gap-2">
				<Icon
					name={hasFailed ? "TriangleAlert" : isUploading ? "Upload" : "CircleCheck"}
					className={hasFailed ? "stroke-red-500" : undefined}
				/>
				<span className="font-medium truncate">{label}</span>
			</div>

			{/* A recording that stopped itself needs to say so. The author did not press stop, and
			    an interface that simply moves on leaves them to guess whether it crashed. */}
			{didReachLimit && (
				<p className="text-amber-600 text-xs dark:text-amber-400">
					{t("video_recorder.stopped_at_limit", { minutes: String(MAX_RECORDING_MINUTES) })}
				</p>
			)}

			{isUploading && (
				<div className="v-stack gap-1 **:tabular-nums">
					<ProgressMeter value={progress * 100} />
					<p className="label">{t("videos.uploading")}</p>
				</div>
			)}

			{hasFailed && <p className="label">{t("videos.upload_failed")}</p>}

			{!isUploading && !hasFailed && <p className="label">{t("video_recorder.can_trim_later")}</p>}

			<div className="h-stack gap-2">
				{hasFailed ? (
					<Button
						size="xs"
						variant="primary"
						className="flex-1"
						onClick={onRetry}
						icon={<Icon name="RotateCcw" />}
					>
						{t("video_recorder.retry_upload")}
					</Button>
				) : (
					<Button
						size="xs"
						variant="white"
						className="flex-1"
						onClick={onOpenTarget}
						isDisabled={isUploading}
						icon={<Icon name="ArrowRight" />}
					>
						{t("video_recorder.open_target")}
					</Button>
				)}
				<Button
					size="xs"
					variant="ghost"
					onClick={onDismiss}
					icon={<Icon name="X" />}
				>
					{t(hasFailed ? "video_recorder.discard_recording" : "general.close")}
				</Button>
			</div>
		</div>
	);
}
